import {
  API_TYPES,
  type CancellationProtectionPayoutIncludedShift,
  type CancellationProtectionPayoutIncludedWorkplace,
  type CancellationProtectionPayoutResource,
  type ListCancellationProtectionPayoutsResponse,
} from "@clipboard-health/contract-backend-main";

import { type ResolvedCancellationProtectionPayout } from "./types";

export function getTotalPayoutAmountInMinorUnits(
  payout: Pick<
    ResolvedCancellationProtectionPayout,
    "originalPayRate" | "originalPaidHours" | "bonusAmount"
  >
): number {
  return (
    payout.originalPayRate.amountInMinorUnits * payout.originalPaidHours +
    payout.bonusAmount.amountInMinorUnits
  );
}

export function findIncludedShift(
  included: ListCancellationProtectionPayoutsResponse["included"],
  shiftId: string
): CancellationProtectionPayoutIncludedShift | undefined {
  return included?.find(
    (r): r is CancellationProtectionPayoutIncludedShift =>
      r.type === API_TYPES.shift && r.id === shiftId
  );
}

export function findIncludedWorkplace(
  included: ListCancellationProtectionPayoutsResponse["included"],
  workplaceId: string
): CancellationProtectionPayoutIncludedWorkplace | undefined {
  return included?.find(
    (r): r is CancellationProtectionPayoutIncludedWorkplace =>
      r.type === API_TYPES.workplace && r.id === workplaceId
  );
}

function resolvePayoutResource(
  payout: CancellationProtectionPayoutResource,
  included: ListCancellationProtectionPayoutsResponse["included"]
): ResolvedCancellationProtectionPayout {
  const shift = findIncludedShift(included, payout.relationships.shift.data.id);
  const workplace = findIncludedWorkplace(included, payout.relationships.workplace.data.id);

  return {
    id: payout.id,
    acknowledged: payout.attributes.acknowledged,
    shift: shift
      ? {
          start: shift.attributes.start,
          end: shift.attributes.end,
          qualification: shift.attributes.qualification,
          cancelledAt: shift.attributes.cancelledAt,
        }
      : undefined,
    workplace: workplace
      ? {
          name: workplace.attributes.name,
          tmz: workplace.attributes.tmz,
        }
      : undefined,
    sourceEvent: payout.attributes.sourceEvent,
    originalPaidHours: payout.attributes.originalPaidHours,
    originalPayRate: payout.attributes.originalPayRate,
    protectedHours: payout.attributes.protectedHours,
    bonusAmount: payout.attributes.bonusAmount,
  };
}

export function resolveCancellationProtectionPayoutsResponse(
  page: ListCancellationProtectionPayoutsResponse
): ResolvedCancellationProtectionPayout[] {
  return page.data.map((payout) => resolvePayoutResource(payout, page.included));
}
